<template>
  <div>
    <app-layout>
      <template v-slot:header>
        <Header
          :title="$t('u_pass_periods_title')"
          :isNewButton="checkPermission('upassperiod_store')"
          @new-button-click="openAddModal"
          @filter-div-status="datatable.filterStatus = $event"
        />
      </template>
      <template v-slot:header-mobile>
        <HeaderMobile
          :title="$t('u_pass_periods_title')"
          :isNewButton="checkPermission('upassperiod_store')"
          @new-button-click="openAddModal"
          @filter-div-status="datatable.filterStatus = $event"
        />
      </template>

      <div class="container-fluid py-3">
        <b-row class="mb-4">
          <b-col md="4" sm="6" class="mb-3 mb-md-0">
            <b-card class="shadow-sm border-left-primary h-100">
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <div class="text-muted small font-weight-bold text-uppercase mb-1">
                    {{ $t('u_pass_total_inventory') }}
                  </div>
                  <div class="h3 mb-0 font-weight-bold text-primary">
                    {{ metrics.totalInventory }}
                  </div>
                </div>
                <div class="p-3 bg-light-primary rounded-circle">
                  <i class="ri-stack-line ri-2x text-primary"></i>
                </div>
              </div>
            </b-card>
          </b-col>

          <b-col md="4" sm="6" class="mb-3 mb-md-0">
            <b-card class="shadow-sm border-left-success h-100">
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <div class="text-muted small font-weight-bold text-uppercase mb-1">
                    {{ $t('u_pass_purchased_count') }}
                  </div>
                  <div class="h3 mb-0 font-weight-bold text-success">
                    {{ metrics.purchasedCount }}
                  </div>
                </div>
                <div class="p-3 bg-light-success rounded-circle">
                  <i class="ri-shopping-cart-check-line ri-2x text-success"></i>
                </div>
              </div>
            </b-card>
          </b-col>

          <b-col md="4" sm="12">
            <b-card class="shadow-sm border-left-info h-100">
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <div class="text-muted small font-weight-bold text-uppercase mb-1">
                    {{ $t('u_pass_remaining_inventory') }}
                  </div>
                  <div class="h3 mb-0 font-weight-bold text-info">
                    {{ metrics.remainingInventory }}
                  </div>
                </div>
                <div class="p-3 bg-light-info rounded-circle">
                  <i class="ri-ticket-2-line ri-2x text-info"></i>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>

        <datatable-filter v-show="datatable.filterStatus" @filter="filter" @filterClear="filterClear">
          <b-row>
            <b-col md="4">
              <b-form-group :label="$t('semester')">
                <semesters-selectbox
                  :multiple="false"
                  v-model="datatable.queryParams.filter.semester_id"
                />
              </b-form-group>
            </b-col>

            <b-col md="4">
              <b-form-group :label="$t('status')">
                <status-selectbox
                  v-model="datatable.queryParams.filter.is_active"
                  :valueType="'num'"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </datatable-filter>

        <datatable
          :isLoading.sync="datatable.isLoading"
          :columns="datatable.columns"
          :rows="datatable.rows"
          :total="datatable.total"
          :queryParams="datatable.queryParams"
          :lineNumbers="false"
          @on-page-change="onPageChange"
          @on-sort-change="onSortChange"
          @on-per-page-change="onPerPageChange"
          v-show="datatable.showTable"
        />
      </div>

      <CommonModal ref="periodModal" size="lg">
        <template v-slot:CommonModalTitle>
          <div>{{ selectedId == 0 ? $t('add_new_period') : $t('edit_period') }}</div>
        </template>
        <template v-slot:CommonModalContent>
          <ValidationObserver ref="periodForm">
            <b-form @submit.prevent="savePeriod">
              <b-row>
                <b-col md="6">
                  <ValidationProvider name="semester_id" rules="required" v-slot="{ errors }">
                    <b-form-group :label="$t('semester')">
                      <semesters-selectbox
                        :multiple="false"
                        :validate-error="errors[0]"
                        :setActive="true"
                        v-model="form.semester_id"
                      />
                    </b-form-group>
                  </ValidationProvider>
                </b-col>

                <b-col md="6">
                  <ValidationProvider name="price" rules="required|min_value:0" v-slot="{ errors, valid }">
                    <b-form-group :label="$t('price')">
                      <b-form-input
                        type="number"
                        step="0.01"
                        v-model.number="form.price"
                        :state="errors[0] ? false : (valid ? true : null)"
                      />
                      <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                  </ValidationProvider>
                </b-col>

                <b-col md="6">
                  <ValidationProvider name="start_date" rules="required" v-slot="{ errors }">
                    <b-form-group :label="$t('start_date')">
                      <select-date
                        v-model="form.start_date"
                        :validationError="errors[0]"
                      />
                    </b-form-group>
                  </ValidationProvider>
                </b-col>

                <b-col md="6">
                  <ValidationProvider name="end_date" rules="required" v-slot="{ errors }">
                    <b-form-group :label="$t('end_date')">
                      <select-date
                        v-model="form.end_date"
                        :validationError="errors[0]"
                      />
                    </b-form-group>
                  </ValidationProvider>
                </b-col>

                <b-col md="6">
                  <ValidationProvider name="total_inventory" rules="required|min_value:1" v-slot="{ errors, valid }">
                    <b-form-group :label="$t('total_inventory')">
                      <b-form-input
                        type="number"
                        v-model.number="form.total_inventory"
                        :state="errors[0] ? false : (valid ? true : null)"
                      />
                      <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                  </ValidationProvider>
                </b-col>
                  <b-col md="6">
                      <ValidationProvider name="student_types" rules="" v-slot="{ errors, valid }">
                          <b-form-group :label="$t('student_type')">
                              <ParameterSelectbox v-model="form.student_types" :multiple="true" :validateError="errors[0]" code="u_pass_student_types" />
                          </b-form-group>
                      </ValidationProvider>
                  </b-col>
                <b-col md="6">
                  <ValidationProvider name="is_active" rules="required" v-slot="{ errors }">
                    <b-form-group :label="$t('status')">
                      <status-selectbox
                        v-model="form.is_active"
                        :valueType="'num'"
                        :validate-error="errors[0]"
                      />
                    </b-form-group>
                  </ValidationProvider>
                </b-col>

                <b-col cols="12" class="mt-4">
                  <div class="d-flex align-items-center justify-content-end">
                    <b-button type="button" variant="outline-secondary" class="mr-2" @click="closeModal">
                      {{ $t('cancel') }}
                    </b-button>
                    <b-button type="submit" variant="primary" :disabled="saving">
                      <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                      {{ $t('save') }}
                    </b-button>
                  </div>
                </b-col>
              </b-row>
            </b-form>
          </ValidationObserver>
        </template>
      </CommonModal>
    </app-layout>
  </div>
</template>

<script>
import AppLayout from "@/layouts/AppLayout";
import Header from "@/layouts/AppLayout/Header";
import HeaderMobile from "@/layouts/AppLayout/HeaderMobile";
import DatatableFilter from "@/components/datatable/DatatableFilter";
import Datatable from "@/components/datatable/Datatable";
import CommonModal from "@/components/elements/CommonModal";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import UPassService from "@/services/UPassService";
import qs from "qs";
import moment from "moment";

export default {
  name: "AdminUPassPeriods",
  components: {
    AppLayout,
    Header,
    HeaderMobile,
    DatatableFilter,
    Datatable,
    CommonModal,
    ValidationObserver,
    ValidationProvider
  },
  metaInfo() {
    return {
      title: this.$t("u_pass_periods_title")
    };
  },
  data() {
    return {
      selectedId: 0,
      saving: false,
      metrics: {
        totalInventory: 0,
        purchasedCount: 0,
        remainingInventory: 0
      },
      form: {
        semester_id: null,
        start_date: "",
        end_date: "",
        total_inventory: null,
        price: null,
        is_active: 1, student_types: []
      },
      datatable: {
        isLoading: false,
        filterStatus: true,
        showTable: true,
        columns: [
          {
            label: "",
            field: "buttons",
            html: true,
            sortable: false,
            tdClass: "p-0 text-center w-40 align-middle",
            buttons: [
              {
                text: this.$t("edit"),
                class: "ri-edit-box-line",
                permission: "upassperiod_update",
                callback: (row) => {
                  this.openEditModal(row);
                }
              }
            ]
          },
          {
            label: this.$t("id"),
            field: "id",
            sortable: true
          },
          {
            label: this.$t("academic_year"),
            field: "academic_year",
            sortable: true
          },
          {
            label: this.$t("start_date"),
            field: "start_date",
            sortable: true,
            formatFn: (value) => {
                return moment(value).format('YYYY-MM-DD');
            }
          },
          {
            label: this.$t("end_date"),
            field: "end_date",
            sortable: true,
            formatFn: (value) => {
               return moment(value).format('YYYY-MM-DD');
            }
          },
            {
                label: this.$t("student_type"),
                field: "student_types_name",
                sortable: true,
            },
          {
            label: this.$t("price"),
            field: "price",
            sortable: true,
            formatFn: (value) => {
              return (value !== null && typeof value !== 'undefined') ? this.formatToCurrency(value) : 'N/A';
            }
          },
          {
            label: this.$t("inventory"),
            field: "inventory",
            sortable: false,
            html: true,
            formatFn: (value, row) => {
              return `<span class="font-weight-bold">${row.inventory.available} / ${row.inventory.total}</span> <small class="text-muted ml-1">(${row.inventory.used} used)</small>`;
            }
          },
          {
            label: this.$t("status"),
            field: "is_active",
            sortable: true,
            html: true,
            formatFn: (value, row) => {
              const isActive = !!(typeof value !== 'undefined' ? value : row.is_active);
              const badgeClass = isActive ? "badge badge-success" : "badge badge-danger";
              const text = isActive ? this.$t("active") : this.$t("passive");
              return `<span class="${badgeClass}">${text}</span>`;
            }
          }
        ],
        rows: [],
        total: 0,
        queryParams: {
          filter: {},
          sort: "-id",
          page: 1,
          limit: 20
        }
      }
    };
  },
  created() {
    this.getRows();
  },
  methods: {
    filter() {
      this.datatable.queryParams.page = 1;
      this.getRows();
    },
    filterClear() {
      this.datatable.queryParams.filter = {};
      this.getRows();
    },
    onPageChange(params) {
      this.datatable.queryParams.page = params.currentPage;
      this.getRows();
    },
    onPerPageChange(params) {
      this.datatable.queryParams.limit = params.currentPerPage;
      this.datatable.queryParams.page = 1;
      this.getRows();
    },
    onSortChange(params) {
      const sortType = params[0].type === "desc" ? "-" : "";
      this.datatable.queryParams.sort = sortType + params[0].field;
      this.getRows();
    },
    async getRows() {
      this.datatable.showTable = true;
      this.datatable.isLoading = true;

      const config = {
        params: {
          ...this.datatable.queryParams
        },
        paramsSerializer: (params) => qs.stringify(params, { encode: false })
      };
      try {
        const res = await UPassService.getPeriods(config);
          const rows = res.data?.data || [];
          this.datatable.rows = rows.map(r => ({
              ...r,
          }));
          this.datatable.total = res.data?.pagination?.total

          let totalInv = 0;
          let purCount = 0;
          this.datatable.rows.forEach(r => {
              if (r.is_active) {
                  totalInv += Number(r.inventory.total || 0);
                  purCount += Number(r.inventory.used || 0);
              }
          });
          this.metrics = {
              totalInventory: totalInv,
              purchasedCount: purCount,
              remainingInventory: Math.max(0, totalInv - purCount)
          };
      } catch (error) {
          this.showErrors(error)
      } finally {
        this.datatable.isLoading = false;
      }
    },
    openAddModal() {
      if (!this.checkPermission("upassperiod_store")) return;
      this.selectedId = 0;
      this.form = {
        semester_id: null,
        start_date: "",
        end_date: "",
        total_inventory: null,
        price: null,
        is_active: 1
      };
      if (this.$refs.periodForm) {
        this.$refs.periodForm.reset();
      }
      this.$refs.periodModal.$refs.commonModal.show();
    },
    async openEditModal(row) {
      if (!this.checkPermission("upassperiod_show") && !this.checkPermission("upassperiod_update")) return;
      this.selectedId = row.id;
      try {
        const res = await UPassService.getPeriod(row.id);
        const data = res.data?.data || res.data || row;
        this.form = {
          id: data.id,
          semester_id: data.semester_id,
          start_date: moment(data.start_date).format("YYYY-MM-DD"),
          end_date: moment(data.end_date).format("YYYY-MM-DD"),
          total_inventory: data.total_inventory,
          price: data.price,
          is_active: data.is_active ? 1 : 0,
          student_types: data.student_types || []
        };
      } catch (e) {
        this.form = {
          ...row,
          start_date: this.toLocaleDate(row.start_date),
          end_date: this.toLocaleDate(row.end_date),
          is_active: row.is_active ? 1 : 0
        };
      }
      if (this.$refs.periodForm) {
        this.$refs.periodForm.reset();
      }
      this.$refs.periodModal.$refs.commonModal.show();
    },
    closeModal() {
      this.$refs.periodModal.$refs.commonModal.hide();
    },
    async savePeriod() {
      const isValid = await this.$refs.periodForm.validate();
      if (!isValid) return;

      if (this.selectedId === 0 && !this.checkPermission("upassperiod_store")) return;
      if (this.selectedId !== 0 && !this.checkPermission("upassperiod_update")) return;

      this.saving = true;
      const payload = {
        ...this.form,
        start_date:moment(this.form.start_date).format("MM/DD/YYYY"),
        end_date: moment(this.form.end_date).format("MM/DD/YYYY")
      };

      try {
        if (this.selectedId === 0) {
          await UPassService.createPeriod(payload);
          this.$toast.success(this.$t('period_created'));
        } else {
          await UPassService.updatePeriod(this.selectedId, payload);
          this.$toast.success(this.$t('period_updated'));
        }
        this.closeModal();
        await this.getRows();
      } catch (error) {
          this.showErrors(error,this.$refs.periodForm)
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
